import { cn } from '@lib/util/cn';
import { ChevronDown } from '@modules/common/icons';
import type { OptionValueAvailability } from '@modules/products/utils/variant-options';
import type React from 'react';

type OptionSelectDropdownProps = {
  optionId: string;
  title: string;
  values: string[];
  current: string | undefined;
  updateOption: (optionId: string, value: string) => void;
  disabled: boolean;
  // Per-value buyability/stock, keyed by option value. When omitted, every
  // value renders as before. See `getOptionValueAvailabilities`.
  valueStates?: Record<string, OptionValueAvailability>;
  'data-testid'?: string;
};

const OptionSelectDropdown: React.FC<OptionSelectDropdownProps> = ({
  optionId,
  title,
  values,
  current,
  updateOption,
  disabled,
  valueStates,
  'data-testid': dataTestId,
}) => {
  const selectId = `option-select-${optionId}`;
  const displayTitle = title === 'Variant' ? 'Option' : title;

  return (
    <div className="flex flex-col gap-y-1">
      <label htmlFor={selectId} className="sr-only">
        Select {displayTitle}
      </label>
      <span
        className="text-sm text-brand-primary-800"
        aria-hidden="true"
        translate="no"
      >
        {displayTitle}
      </span>
      <div className="relative" data-testid={dataTestId}>
        <select
          id={selectId}
          value={current ?? ''}
          onChange={(e) => {
            updateOption(optionId, e.target.value);
            e.currentTarget.blur();
          }}
          disabled={disabled}
          className={cn(
            'w-full h-11 appearance-none cursor-pointer',
            'border border-ui-border-base rounded-2 bg-ui-bg-subtle',
            'pl-3 pr-10 nw-typography-paragraph text-ui-fg-base',
            'focus:outline-hidden focus:ring-2 focus:ring-fg-interactive-strong focus:border-fg-interactive-strong',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          data-testid="option-select"
          translate="no"
        >
          <option value="" disabled>
            Select {displayTitle}
          </option>
          {values.map((v) => {
            const state = valueStates?.[v];
            const isUnbuyable = state ? !state.buyable : false;
            // Out of stock stays selectable so the back-in-stock CTA is reachable.
            const isOutOfStock = state
              ? state.buyable && !state.inStock
              : false;
            const suffix = isUnbuyable ? ' — Unavailable' : '';
            return (
              <option
                key={v}
                value={v}
                disabled={isUnbuyable}
                // Crossed out to match the option-tile treatment; the
                // "Out Of Stock" badge next to the price carries the label.
                style={
                  isUnbuyable || isOutOfStock
                    ? { textDecoration: 'line-through' }
                    : undefined
                }
              >
                {v}
                {suffix}
              </option>
            );
          })}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-brand-primary-900"
          size="24"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default OptionSelectDropdown;
